import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Checkbox,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import {
  CheckCircle2 as CheckCircle2Icon,
  ClipboardList as ClipboardListIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Eye as EyeIcon,
  FileText as FileTextIcon,
  Mail as MailIcon,
  PauseCircle as PauseCircleIcon,
  Phone as PhoneIcon,
  Plus as PlusIcon,
  RefreshCcw as RefreshIcon,
  Search as SearchIcon,
  Trash2 as DeleteIcon,
  Upload as UploadIcon,
  User as UserIcon,
  XCircle as XCircleIcon
} from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../services/api';

const StyledCard = styled(Card)(() => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden'
}));

const EMPTY_FORM = {
  titolo: '',
  template: null,
  templateVersion: null,
  customer: null,
  stato: 'bozza',
  assegnatario: null,
  note_interne: ''
};

const SORTABLE_COLUMNS = {
  titolo: 'titolo',
  template_nome: 'template_nome',
  cliente_nome: 'cliente_nome',
  stato: 'stato',
  assegnatario_nome: 'assegnatario_nome',
  updated_at: 'updated_at'
};

const PRACTICE_STATUS_CHIPS = {
  bozza: { color: 'warning', variant: 'outlined' },
  in_revisione: { color: 'info', variant: 'filled' },
  approvata: { color: 'success', variant: 'filled' },
  annullata: { color: 'error', variant: 'outlined' }
};

const getGenerationProgressLabel = (progress, phase) => {
  const normalizedPhase = String(phase || '').trim();
  if (normalizedPhase) return normalizedPhase;
  if (progress < 20) return 'Preparazione del contesto pratica';
  if (progress < 45) return 'Analisi dei documenti caricati';
  if (progress < 70) return 'Composizione del prompt finale';
  if (progress < 90) return 'Generazione della bozza AI';
  return 'Rifinitura e salvataggio del risultato';
};

const getGenerationProgressChipColor = (progress) => {
  if (progress < 20) return 'warning';
  if (progress < 90) return 'info';
  return 'success';
};

const formatGenerationTokenValue = (value) => {
  if (value == null || value === '') {
    return '-';
  }
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return String(value);
  }
  return numericValue.toLocaleString('it-IT');
};

const formatDateTimeIt = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = String(date.getFullYear());
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

const normalizeCustomerLabel = (customer) => {
  if (!customer) return '';
  const label = String(customer.full_name || '').trim();
  if (label) return label;
  if (customer.id) return `Cliente #${customer.id}`;
  return '';
};

const StatWidget = ({ title, value, icon: Icon, color }) => (
  <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', height: '100%' }} elevation={0} variant="outlined">
    <Box
      sx={{
        p: 1.5,
        borderRadius: '50%',
        bgcolor: `${color}15`,
        color,
        mr: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <Icon size={24} />
    </Box>
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
        {title}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
        {value}
      </Typography>
    </Box>
  </Paper>
);

export default function AttiAiPratichePage() {
  const theme = useTheme();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({
    stati: [],
    templates: [],
    assegnatari: []
  });
  const [stats, setStats] = useState({
    total_count: 0,
    bozza_count: 0,
    in_revisione_count: 0,
    approvata_count: 0,
    annullata_count: 0
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [documentsDialogOpen, setDocumentsDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [documentDeleteDialogOpen, setDocumentDeleteDialogOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [documentDeleteTarget, setDocumentDeleteTarget] = useState(null);
  const [selectedPracticeForDocuments, setSelectedPracticeForDocuments] = useState(null);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [documentsSaving, setDocumentsSaving] = useState(false);
  const [demoDocumentsSubmitting, setDemoDocumentsSubmitting] = useState(false);
  const [generationSubmitting, setGenerationSubmitting] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [generationPhase, setGenerationPhase] = useState('');
  const [activeGenerationId, setActiveGenerationId] = useState(null);
  const [latestGenerationResult, setLatestGenerationResult] = useState(null);
  const [practiceGenerations, setPracticeGenerations] = useState([]);
  const [practiceGenerationsLoading, setPracticeGenerationsLoading] = useState(false);
  const [practiceGenerationStats, setPracticeGenerationStats] = useState({
    total_count: 0,
    completata_count: 0,
    in_corso_count: 0,
    errore_count: 0
  });
  const [documentsDialogTab, setDocumentsDialogTab] = useState('documents');
  const [includeOptionalDemo, setIncludeOptionalDemo] = useState(false);
  const [documentsData, setDocumentsData] = useState({
    checklist: [],
    freeDocuments: [],
    summary: {
      totalTemplateDocuments: 0,
      totalRequiredDocuments: 0,
      uploadedTemplateDocuments: 0,
      uploadedRequiredDocuments: 0,
      freeDocuments: 0,
      totalUploadedFiles: 0
    }
  });
  const [customerOptions, setCustomerOptions] = useState([]);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [customerInputValue, setCustomerInputValue] = useState('');

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedAssignee, setSelectedAssignee] = useState(null);
  const [statoFilter, setStatoFilter] = useState('all');

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [orderBy, setOrderBy] = useState('updated_at');
  const [order, setOrder] = useState('desc');

  const [form, setForm] = useState(EMPTY_FORM);

  const templateOptions = useMemo(() => meta.templates || [], [meta.templates]);
  const assigneeOptions = useMemo(() => meta.assegnatari || [], [meta.assegnatari]);
  const statoOptions = useMemo(() => meta.stati || [], [meta.stati]);
  const missingRequiredChecklistItems = useMemo(
    () =>
      (documentsData.checklist || []).filter(
        (item) => Boolean(item?.obbligatorio) && Number(item?.uploadedCount || 0) <= 0
      ),
    [documentsData.checklist]
  );
  const missingDemoChecklistItems = useMemo(() => {
    const checklist = documentsData.checklist || [];
    if (includeOptionalDemo) {
      return checklist.filter((item) => Number(item?.uploadedCount || 0) <= 0);
    }
    return checklist.filter((item) => Boolean(item?.obbligatorio) && Number(item?.uploadedCount || 0) <= 0);
  }, [documentsData.checklist, includeOptionalDemo]);
  const generationReady = useMemo(
    () => missingRequiredChecklistItems.length === 0,
    [missingRequiredChecklistItems.length]
  );

  const resetForm = useCallback(() => {
    setForm(EMPTY_FORM);
    setCustomerOptions([]);
    setCustomerInputValue('');
  }, []);

  const fetchMeta = useCallback(async () => {
    try {
      const response = await api.get('/gtl-pratiche/meta');
      setMeta({
        stati: response.data?.stati || [],
        templates: response.data?.templates || [],
        assegnatari: response.data?.assegnatari || []
      });
    } catch (err) {
      console.error('Errore caricamento meta pratiche AI:', err);
      toast.error(err?.response?.data?.message || 'Errore nel caricamento dei metadati pratiche AI');
    }
  }, []);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        orderBy,
        orderDirection: order
      };

      if (searchTerm.trim()) params.search = searchTerm.trim();
      if (selectedTemplate?.id) params.templateId = selectedTemplate.id;
      if (selectedAssignee?.id) params.assegnatarioId = selectedAssignee.id;
      if (statoFilter !== 'all') params.stato = statoFilter;

      const response = await api.get('/gtl-pratiche', { params });
      setRows(response.data?.data || []);
      setTotalCount(Number(response.data?.meta?.totalCount || 0));
      setStats({
        total_count: Number(response.data?.stats?.total_count || 0),
        bozza_count: Number(response.data?.stats?.bozza_count || 0),
        in_revisione_count: Number(response.data?.stats?.in_revisione_count || 0),
        approvata_count: Number(response.data?.stats?.approvata_count || 0),
        annullata_count: Number(response.data?.stats?.annullata_count || 0)
      });
      setError('');
    } catch (err) {
      const message = err?.response?.data?.message || 'Errore nel caricamento delle pratiche AI';
      console.error('Errore caricamento pratiche AI:', err);
      setError(message);
      toast.error(message);
      if (page > 0) {
        setPage((currentPage) => (currentPage > 0 ? currentPage - 1 : 0));
      } else {
        setRows([]);
        setTotalCount(0);
      }
    } finally {
      setLoading(false);
    }
  }, [order, orderBy, page, rowsPerPage, searchTerm, selectedAssignee, selectedTemplate, statoFilter]);

  useEffect(() => {
    fetchMeta();
  }, [fetchMeta]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  useEffect(() => {
    if (!dialogOpen) {
      return undefined;
    }

    const term = String(customerInputValue || '').trim();
    const timeoutId = window.setTimeout(async () => {
      if (!term) {
        setCustomerOptions(form.customer ? [form.customer] : []);
        return;
      }

      setCustomerLoading(true);
      try {
        const response = await api.get('/gtl-pratiche/customers', {
          params: {
            search: term,
            limit: 20
          }
        });

        const options = Array.isArray(response.data) ? response.data : [];
        const currentCustomer = form.customer ? [form.customer] : [];
        const merged = [...currentCustomer, ...options];
        const byId = new Map();
        merged.forEach((item) => {
          if (item?.id) {
            byId.set(String(item.id), item);
          }
        });
        setCustomerOptions(Array.from(byId.values()));
      } catch (err) {
        console.error('Errore lookup clienti pratiche AI:', err);
      } finally {
        setCustomerLoading(false);
      }
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [customerInputValue, dialogOpen, form.customer]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleTemplateSelection = useCallback((template) => {
    const templateVersion = template?.versione_corrente_id
      ? {
          id: template.versione_corrente_id,
          numero_versione: template.versione_corrente_numero || null
        }
      : null;

    setForm((prev) => ({
      ...prev,
      template: template || null,
      templateVersion
    }));
  }, []);

  const handleOpenCreate = () => {
    setEditingRow(null);
    resetForm();
    setDialogOpen(true);
  };

  const handleOpenEdit = (row) => {
    setEditingRow(row);

    const selectedTemplateOption =
      templateOptions.find((item) => Number(item.id) === Number(row.template_id)) || {
        id: row.template_id,
        nome: row.template_nome,
        tipo_atto: row.template_tipo_atto,
        categoria: row.template_categoria,
        versione_corrente_id: row.template_versione_id,
        versione_corrente_numero: row.template_versione_numero
      };

    const selectedAssigneeOption =
      assigneeOptions.find((item) => Number(item.id) === Number(row.assegnatario_id)) || null;

    const selectedCustomerOption = row?.cliente
      ? {
          id: row.cliente.id,
          full_name: row.cliente.nome,
          email: row.cliente.email,
          telephone: row.cliente.telefono
        }
      : row?.cliente_id
      ? {
          id: row.cliente_id,
          full_name: row.cliente_nome || `ID ${row.cliente_id}`
        }
      : null;

    setForm({
      titolo: row.titolo || '',
      template: selectedTemplateOption,
      templateVersion: {
        id: row.template_versione_id,
        numero_versione: row.template_versione_numero || null
      },
      customer: selectedCustomerOption,
      stato: row.stato || 'bozza',
      assegnatario: selectedAssigneeOption,
      note_interne: row.note_interne || ''
    });
    setCustomerOptions(selectedCustomerOption ? [selectedCustomerOption] : []);
    setCustomerInputValue(selectedCustomerOption?.full_name || '');
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    if (saving) return;
    setDialogOpen(false);
    setEditingRow(null);
    resetForm();
  };

  const handleSave = async () => {
    if (!form.titolo.trim()) {
      toast.error('Il titolo della pratica e obbligatorio');
      return;
    }
    if (!form.template?.id) {
      toast.error('Seleziona un template');
      return;
    }
    if (!form.templateVersion?.id) {
      toast.error('Il template selezionato non ha una versione corrente disponibile');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        titolo: form.titolo.trim(),
        template_id: form.template.id,
        template_versione_id: form.templateVersion.id,
        cliente_id: form.customer?.id || null,
        stato: form.stato,
        assegnatario_id: form.assegnatario?.id || null,
        note_interne: form.note_interne.trim()
      };

      if (editingRow) {
        await api.put(`/gtl-pratiche/${editingRow.id}`, payload);
        toast.success('Pratica AI aggiornata correttamente');
      } else {
        await api.post('/gtl-pratiche', payload);
        toast.success('Pratica AI creata correttamente');
      }

      handleCloseDialog();
      await fetchRows();
    } catch (err) {
      console.error('Errore salvataggio pratica AI:', err);
      toast.error(err?.response?.data?.message || 'Errore nel salvataggio della pratica AI');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await api.delete(`/gtl-pratiche/${deleteTarget.id}`);
      toast.success('Pratica AI eliminata correttamente');
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      await fetchRows();
    } catch (err) {
      console.error('Errore eliminazione pratica AI:', err);
      toast.error(err?.response?.data?.message || 'Errore nell\'eliminazione della pratica AI');
    } finally {
      setSaving(false);
    }
  };

  const handleQuickStatusChange = async (row, nextStatus) => {
    if (!row?.id || !nextStatus || row.stato === nextStatus) {
      return;
    }

    setSaving(true);
    try {
      const payload = {
        titolo: row.titolo || '',
        template_id: row.template_id,
        template_versione_id: row.template_versione_id,
        cliente_id: row.cliente_id || null,
        stato: nextStatus,
        assegnatario_id: row.assegnatario_id || null,
        note_interne: row.note_interne || ''
      };

      await api.put(`/gtl-pratiche/${row.id}`, payload);
      toast.success(
        nextStatus === 'approvata'
          ? 'Pratica AI impostata su Approvata'
          : 'Pratica AI impostata su Annullata'
      );

      if (selectedPracticeForDocuments?.id === row.id) {
        setSelectedPracticeForDocuments((prev) => (prev ? { ...prev, stato: nextStatus } : prev));
      }

      await fetchRows();
    } catch (err) {
      console.error('Errore aggiornamento rapido stato pratica AI:', err);
      toast.error(err?.response?.data?.message || 'Errore nell\'aggiornamento dello stato pratica AI');
    } finally {
      setSaving(false);
    }
  };

  const fetchDocuments = useCallback(async (praticaId) => {
    setDocumentsLoading(true);
    try {
      const response = await api.get(`/gtl-pratiche/${praticaId}/documents`);
      setDocumentsData({
        checklist: response.data?.checklist || [],
        freeDocuments: response.data?.freeDocuments || [],
        summary: response.data?.summary || {
          totalTemplateDocuments: 0,
          totalRequiredDocuments: 0,
          uploadedTemplateDocuments: 0,
          uploadedRequiredDocuments: 0,
          freeDocuments: 0,
          totalUploadedFiles: 0
        }
      });
    } catch (err) {
      console.error('Errore caricamento documenti pratica AI:', err);
      toast.error(err?.response?.data?.message || 'Errore nel caricamento dei documenti della pratica AI');
    } finally {
      setDocumentsLoading(false);
    }
  }, []);

  const fetchPracticeGenerations = useCallback(async (praticaId) => {
    setPracticeGenerationsLoading(true);
    try {
      const response = await api.get(`/gtl-pratiche/${praticaId}/generations`);
      setPracticeGenerations(response.data?.data || []);
      setPracticeGenerationStats({
        total_count: Number(response.data?.stats?.total_count || 0),
        completata_count: Number(response.data?.stats?.completata_count || 0),
        in_corso_count: Number(response.data?.stats?.in_corso_count || 0),
        errore_count: Number(response.data?.stats?.errore_count || 0)
      });
    } catch (err) {
      console.error('Errore caricamento generazioni pratica AI:', err);
      setPracticeGenerations([]);
      setPracticeGenerationStats({
        total_count: 0,
        completata_count: 0,
        in_corso_count: 0,
        errore_count: 0
      });
      toast.error(err?.response?.data?.message || 'Errore nel caricamento dello storico generazioni della pratica AI');
    } finally {
      setPracticeGenerationsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!generationSubmitting || !activeGenerationId || !selectedPracticeForDocuments?.id) {
      return undefined;
    }

    let cancelled = false;
    setGenerationProgress(8);
    setGenerationPhase('Preparazione contesto');

    const tick = async () => {
      if (cancelled) return;
      try {
        const progressResponse = await api.get(`/gtl-generazioni/${activeGenerationId}/progress`).catch(() => null);

        const progressValue = Number(progressResponse?.data?.progress);
        if (!Number.isNaN(progressValue) && Number.isFinite(progressValue)) {
          setGenerationProgress(Math.min(100, Math.max(0, progressValue)));
        }
        setGenerationPhase(String(progressResponse?.data?.phase || '').trim());

        const state = String(progressResponse?.data?.state || '').trim().toLowerCase();
        const shouldCheckDb =
          !progressResponse ||
          state === 'completed' ||
          state === 'failed' ||
          state === 'stuck' ||
          (Number.isFinite(progressValue) && progressValue >= 100);

        if (!shouldCheckDb) {
          return;
        }

        const generationResponse = await api.get(`/gtl-generazioni/${activeGenerationId}`).catch(() => null);
        const row = generationResponse?.data?.data || null;
        if (!row || !row.stato || row.stato === 'in_corso') {
          return;
        }

        await fetchPracticeGenerations(selectedPracticeForDocuments.id);
        await fetchRows();
        setActiveGenerationId(null);
        setGenerationSubmitting(false);
        setGenerationProgress(100);
        setGenerationPhase('');
        if (row.stato === 'completata') {
          setLatestGenerationResult(row);
          setDocumentsDialogTab('result');
          toast.success('Generazione completata');
        } else {
          setDocumentsDialogTab('history');
          toast.error(row.messaggio_errore || 'Generazione non riuscita');
        }
      } catch (_error) {}
    };

    const intervalId = window.setInterval(tick, 1500);
    tick();

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [activeGenerationId, fetchPracticeGenerations, fetchRows, generationSubmitting, selectedPracticeForDocuments?.id]);

  const handleOpenDocuments = async (row) => {
    setSelectedPracticeForDocuments(row);
    setDocumentsDialogTab('documents');
    setDocumentsDialogOpen(true);
    await Promise.all([fetchDocuments(row.id), fetchPracticeGenerations(row.id)]);
  };

  const handleCloseDocumentsDialog = () => {
    if (documentsSaving || generationSubmitting || demoDocumentsSubmitting) return;
    setDocumentsDialogOpen(false);
    setSelectedPracticeForDocuments(null);
    setLatestGenerationResult(null);
    setActiveGenerationId(null);
    setPracticeGenerations([]);
    setPracticeGenerationStats({
      total_count: 0,
      completata_count: 0,
      in_corso_count: 0,
      errore_count: 0
    });
    setDocumentsDialogTab('documents');
    setGenerationProgress(0);
    setGenerationPhase('');
    setIncludeOptionalDemo(false);
    setDemoDocumentsSubmitting(false);
    setDocumentsData({
      checklist: [],
      freeDocuments: [],
      summary: {
        totalTemplateDocuments: 0,
        totalRequiredDocuments: 0,
        uploadedTemplateDocuments: 0,
        uploadedRequiredDocuments: 0,
        freeDocuments: 0,
        totalUploadedFiles: 0
      }
    });
  };

  const handleDocumentUrl = async (documentId, mode = 'preview') => {
    try {
      const endpoint =
        mode === 'download'
          ? `/gtl-pratiche/documents/${documentId}/download-url`
          : `/gtl-pratiche/documents/${documentId}/preview-url`;
      const response = await api.get(endpoint);
      if (response.data?.url) {
        window.open(response.data.url, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      console.error('Errore apertura documento pratica AI:', err);
      toast.error(err?.response?.data?.message || 'Errore nell\'apertura del documento');
    }
  };

  const handleGenerateDemoDocuments = async () => {
    if (!selectedPracticeForDocuments?.id) {
      return;
    }

    setDemoDocumentsSubmitting(true);
    try {
      const response = await api.post(`/gtl-pratiche/${selectedPracticeForDocuments.id}/documents/generate-demo`, {
        includeOptional: includeOptionalDemo
      });

      setDocumentsData({
        checklist: response.data?.checklist || [],
        freeDocuments: response.data?.freeDocuments || [],
        summary: response.data?.summary || {
          totalTemplateDocuments: 0,
          totalRequiredDocuments: 0,
          uploadedTemplateDocuments: 0,
          uploadedRequiredDocuments: 0,
          freeDocuments: 0,
          totalUploadedFiles: 0
        }
      });

      if (Number(response.data?.createdCount || 0) <= 0) {
        toast.info(response.data?.message || 'Nessun documento demo creato');
      } else {
        toast.success(response.data?.message || 'Documenti demo creati correttamente');
      }
    } catch (err) {
      console.error('Errore creazione documenti demo pratica AI:', err);
      toast.error(err?.response?.data?.message || 'Errore nella creazione dei documenti demo');
    } finally {
      setDemoDocumentsSubmitting(false);
    }
  };

  const handleUploadDocument = async (templateDocumentoId, files) => {
    if (!selectedPracticeForDocuments?.id || !files) {
      return;
    }

    setDocumentsSaving(true);
    try {
      const filesList = Array.isArray(files) ? files : Array.from(files);
      let uploadedCount = 0;

      for (const file of filesList) {
        const formData = new FormData();
        formData.append('file', file);
        if (templateDocumentoId) {
          formData.append('template_documento_id', String(templateDocumentoId));
        }

        await api.post(`/gtl-pratiche/${selectedPracticeForDocuments.id}/documents/upload`, formData);
        uploadedCount += 1;
      }

      toast.success(
        uploadedCount === 1
          ? 'Documento pratica AI caricato correttamente'
          : `${uploadedCount} documenti pratica AI caricati correttamente`
      );
      await fetchDocuments(selectedPracticeForDocuments.id);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Errore nel caricamento del documento');
    } finally {
      setDocumentsSaving(false);
    }
  };

  const handleDeleteDocument = async () => {
    if (!selectedPracticeForDocuments?.id || !documentDeleteTarget?.id) return;

    setDocumentsSaving(true);
    try {
      const response = await api.delete(`/gtl-pratiche/documents/${documentDeleteTarget.id}`);
      toast.success(response.data?.message || 'Documento pratica AI eliminato correttamente');
      await fetchDocuments(selectedPracticeForDocuments.id);
      setDocumentDeleteDialogOpen(false);
      setDocumentDeleteTarget(null);
    } catch (err) {
      console.error('Errore eliminazione documento pratica AI:', err);
      toast.error(err?.response?.data?.message || 'Errore nell\'eliminazione del documento');
    } finally {
      setDocumentsSaving(false);
    }
  };

  const handleOpenDeleteDocumentDialog = (documentRow) => {
    if (!documentRow?.id) {
      return;
    }
    setDocumentDeleteTarget(documentRow);
    setDocumentDeleteDialogOpen(true);
  };

  const handleCloseDeleteDocumentDialog = () => {
    if (documentsSaving) {
      return;
    }
    setDocumentDeleteDialogOpen(false);
    setDocumentDeleteTarget(null);
  };

  const handleStartGeneration = async () => {
    if (!generationReady || !selectedPracticeForDocuments?.id) {
      toast.error('Completa prima tutti i documenti obbligatori mancanti');
      return;
    }

    setGenerationSubmitting(true);
    setLatestGenerationResult(null);
    setDocumentsDialogTab('history');
    setGenerationProgress(8);
    setActiveGenerationId(null);

    try {
      const response = await api.post(`/gtl-pratiche/${selectedPracticeForDocuments.id}/generations/start`);
      const generationId = response.data?.generationId || null;

      if (!generationId) {
        toast.error('Generazione avviata ma ID non disponibile');
        setGenerationSubmitting(false);
        return;
      }

      setActiveGenerationId(generationId);
      toast.success(response.data?.message || 'Generazione avviata in background');
      await fetchPracticeGenerations(selectedPracticeForDocuments.id);
    } catch (err) {
      console.error('Errore avvio generazione pratica AI:', err);
      const responseData = err?.response?.data || {};
      const message = responseData?.message || 'Errore nella generazione della pratica AI';
      const detail = responseData?.detail ? `\n${responseData.detail}` : '';
      const suggestion = responseData?.suggestion ? `\n${responseData.suggestion}` : '';

      if (responseData?.generationId) {
        setActiveGenerationId(responseData.generationId);
        toast.info(`${message}${detail}${suggestion}`.trim() || 'Generazione già in corso');
        try {
          await fetchPracticeGenerations(selectedPracticeForDocuments.id);
        } catch (_refreshError) {}
        return;
      }

      toast.error(`${message}${detail}${suggestion}`);
      setGenerationSubmitting(false);
    }
  };

  const handleSelectGenerationFromHistory = (generation) => {
    if (!generation) {
      return;
    }

    setLatestGenerationResult(generation);
    setDocumentsDialogTab('result');
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
          Pratiche
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Gestisci le pratiche operative collegate ai template di Generazione Atti AI.
        </Typography>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <StatWidget title="Totale pratiche" value={stats.total_count} icon={ClipboardListIcon} color="#4f46e5" />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatWidget title="In bozza" value={stats.bozza_count} icon={PauseCircleIcon} color="#f59e0b" />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatWidget title="In revisione" value={stats.in_revisione_count} icon={EyeIcon} color="#2563eb" />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatWidget title="Approvate" value={stats.approvata_count} icon={CheckCircle2Icon} color="#16a34a" />
        </Grid>
      </Grid>

      <StyledCard>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ px: 3, py: 2.25, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Grid container spacing={1.5} alignItems="center">
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Cerca per titolo, template, cliente o assegnatario"
                  value={searchTerm}
                  onChange={(event) => {
                    setSearchTerm(event.target.value);
                    setPage(0);
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon size={16} />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Autocomplete
                  size="small"
                  options={templateOptions}
                  value={selectedTemplate}
                  onChange={(_, value) => {
                    setSelectedTemplate(value || null);
                    setPage(0);
                  }}
                  getOptionLabel={(option) => option?.nome || ''}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => <TextField {...params} label="Template" />}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Stato"
                  value={statoFilter}
                  onChange={(event) => {
                    setStatoFilter(event.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="all">Tutti</MenuItem>
                  {statoOptions.map((item) => (
                    <MenuItem key={item.value} value={item.value}>
                      {item.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={2}>
                <Autocomplete
                  size="small"
                  options={assigneeOptions}
                  value={selectedAssignee}
                  onChange={(_, value) => {
                    setSelectedAssignee(value || null);
                    setPage(0);
                  }}
                  getOptionLabel={(option) => option?.name || ''}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => <TextField {...params} label="Assegnatario" />}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                  <Tooltip title="Ricarica">
                    <IconButton onClick={fetchRows}>
                      <RefreshIcon size={18} />
                    </IconButton>
                  </Tooltip>
                  <Button variant="contained" startIcon={<PlusIcon size={16} />} onClick={handleOpenCreate}>
                    Nuova pratica
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {error ? (
            <Box sx={{ px: 3, pt: 2 }}>
              <Alert severity="error">{error}</Alert>
            </Box>
          ) : null}

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === SORTABLE_COLUMNS.titolo}
                      direction={orderBy === SORTABLE_COLUMNS.titolo ? order : 'asc'}
                      onClick={() => handleRequestSort(SORTABLE_COLUMNS.titolo)}
                    >
                      Titolo
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === SORTABLE_COLUMNS.template_nome}
                      direction={orderBy === SORTABLE_COLUMNS.template_nome ? order : 'asc'}
                      onClick={() => handleRequestSort(SORTABLE_COLUMNS.template_nome)}
                    >
                      Template
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === SORTABLE_COLUMNS.cliente_nome}
                      direction={orderBy === SORTABLE_COLUMNS.cliente_nome ? order : 'asc'}
                      onClick={() => handleRequestSort(SORTABLE_COLUMNS.cliente_nome)}
                    >
                      Cliente
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === SORTABLE_COLUMNS.stato}
                      direction={orderBy === SORTABLE_COLUMNS.stato ? order : 'asc'}
                      onClick={() => handleRequestSort(SORTABLE_COLUMNS.stato)}
                    >
                      Stato
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === SORTABLE_COLUMNS.assegnatario_nome}
                      direction={orderBy === SORTABLE_COLUMNS.assegnatario_nome ? order : 'asc'}
                      onClick={() => handleRequestSort(SORTABLE_COLUMNS.assegnatario_nome)}
                    >
                      Assegnatario
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === SORTABLE_COLUMNS.updated_at}
                      direction={orderBy === SORTABLE_COLUMNS.updated_at ? order : 'asc'}
                      onClick={() => handleRequestSort(SORTABLE_COLUMNS.updated_at)}
                    >
                      Aggiornata
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">Azioni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                        Caricamento pratiche AI...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                        Nessuna pratica AI trovata con i filtri selezionati.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => {
                    const hasGenerations = Number(row.generation_count || 0) > 0;
                    const disableQuickStatusActions = row.stato === 'bozza' && !hasGenerations;

                    return (
                    <TableRow hover key={row.id}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {row.titolo}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{row.template_nome || '-'}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {row.template_versione_numero ? `V${row.template_versione_numero}` : 'Versione n.d.'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{row.cliente_nome || '-'}</Typography>
                        {row?.cliente?.email ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.35, color: 'text.secondary' }}>
                            <MailIcon size={13} />
                            <Typography variant="caption" color="inherit">
                              {row.cliente.email}
                            </Typography>
                          </Box>
                        ) : null}
                        {row?.cliente?.telefono ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.2, color: 'text.secondary' }}>
                            <PhoneIcon size={13} />
                            <Typography variant="caption" color="inherit">
                              {row.cliente.telefono}
                            </Typography>
                          </Box>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={statoOptions.find((item) => item.value === row.stato)?.label || row.stato}
                          color={PRACTICE_STATUS_CHIPS[row.stato]?.color || 'default'}
                          variant={PRACTICE_STATUS_CHIPS[row.stato]?.variant || 'outlined'}
                          sx={{ fontWeight: 600 }}
                        />
                        {hasGenerations ? (
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.75 }}>
                            <Chip size="small" variant="outlined" label={`${row.generation_count} gen.`} sx={{ height: 20, '& .MuiChip-label': { px: 0.75, fontSize: 11 } }} />
                            {Number(row.in_corso_generation_count || 0) > 0 ? (
                              <Chip
                                size="small"
                                color="info"
                                variant="outlined"
                                label={`${row.in_corso_generation_count} in corso`}
                                sx={{ height: 20, '& .MuiChip-label': { px: 0.75, fontSize: 11 } }}
                              />
                            ) : null}
                            {Number(row.errored_generation_count || 0) > 0 ? (
                              <Chip
                                size="small"
                                color="error"
                                variant="outlined"
                                label={`${row.errored_generation_count} errori`}
                                sx={{ height: 20, '& .MuiChip-label': { px: 0.75, fontSize: 11 } }}
                              />
                            ) : null}
                          </Box>
                        ) : null}
                      </TableCell>
                      <TableCell>{row.assegnatario_nome || '-'}</TableCell>
                      <TableCell>{formatDateTimeIt(row.updated_at)}</TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'nowrap', gap: 0.25 }}>
                          <Tooltip title="Documenti">
                            <IconButton size="small" onClick={() => handleOpenDocuments(row)}>
                              <FileTextIcon size={16} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip
                            title={
                              disableQuickStatusActions
                                ? 'Genera prima una bozza per poter approvare la pratica'
                                : row.stato === 'approvata'
                                ? 'Pratica già approvata'
                                : 'Approva pratica'
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                color="success"
                                disabled={saving || row.stato === 'approvata' || disableQuickStatusActions}
                                onClick={() => handleQuickStatusChange(row, 'approvata')}
                              >
                                <CheckCircle2Icon size={16} />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip
                            title={
                              disableQuickStatusActions
                                ? 'Genera prima una bozza per poter annullare la pratica da questo comando rapido'
                                : row.stato === 'annullata'
                                ? 'Pratica già annullata'
                                : 'Annulla pratica'
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                disabled={saving || row.stato === 'annullata' || disableQuickStatusActions}
                                onClick={() => handleQuickStatusChange(row, 'annullata')}
                              >
                                <XCircleIcon size={16} />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Modifica">
                            <IconButton size="small" onClick={() => handleOpenEdit(row)}>
                              <EditIcon size={16} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Elimina">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                setDeleteTarget(row);
                                setDeleteDialogOpen(true);
                              }}
                            >
                              <DeleteIcon size={16} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(_event, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
            labelRowsPerPage="Righe per pagina"
          />
        </CardContent>
      </StyledCard>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} fullWidth maxWidth="md">
        <DialogTitle>{editingRow ? 'Modifica pratica AI' : 'Nuova pratica AI'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Titolo"
                value={form.titolo}
                onChange={(event) => setForm((prev) => ({ ...prev, titolo: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <Autocomplete
                options={templateOptions}
                value={form.template}
                onChange={(_, value) => handleTemplateSelection(value || null)}
                getOptionLabel={(option) => option?.nome || ''}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Template"
                    helperText={form.template?.tipo_atto || 'Seleziona un template attivo da collegare alla pratica'}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Versione congelata"
                value={form.templateVersion?.numero_versione ? `V${form.templateVersion.numero_versione}` : 'Nessuna versione'}
                InputProps={{ readOnly: true }}
                helperText="Usa la versione corrente del template selezionato"
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <Autocomplete
                options={customerOptions}
                value={form.customer}
                loading={customerLoading}
                onChange={(_, value) => setForm((prev) => ({ ...prev, customer: value || null }))}
                onInputChange={(_event, value) => setCustomerInputValue(value)}
                inputValue={customerInputValue}
                filterOptions={(options) => options}
                getOptionLabel={(option) => normalizeCustomerLabel(option)}
                isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                renderOption={(props, option) => (
                  <Box component="li" {...props} sx={{ py: 1, alignItems: 'flex-start' }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        bgcolor: '#eef2ff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        color: 'primary.main',
                        mt: 0.25
                      }}
                    >
                      <UserIcon size={18} />
                    </Box>
                    <Box sx={{ ml: 1.5, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 800 }}>
                        {normalizeCustomerLabel(option)}
                      </Typography>
                      {option?.telephone ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
                          <PhoneIcon size={14} />
                          <Typography variant="body2" color="text.secondary" noWrap>
                            {option.telephone}
                          </Typography>
                        </Box>
                      ) : null}
                      {option?.email ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
                          <MailIcon size={14} />
                          <Typography variant="body2" color="text.secondary" noWrap>
                            {option.email}
                          </Typography>
                        </Box>
                      ) : null}
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Cliente"
                    placeholder="Cerca per cognome, nome, telefono o email"
                    helperText={
                      form.customer ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                          {form.customer?.telephone ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <PhoneIcon size={13} />
                              <Typography variant="caption" color="text.secondary">
                                {form.customer.telephone}
                              </Typography>
                            </Box>
                          ) : null}
                          {form.customer?.email ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <MailIcon size={13} />
                              <Typography variant="caption" color="text.secondary">
                                {form.customer.email}
                              </Typography>
                            </Box>
                          ) : null}
                          {!form.customer?.telephone && !form.customer?.email ? (
                            <Typography variant="caption" color="text.secondary">
                              Cliente selezionato
                            </Typography>
                          ) : null}
                        </Box>
                      ) : (
                        'Lookup remoto sulla tabella customer'
                      )
                    }
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                label="Stato"
                value={form.stato}
                onChange={(event) => setForm((prev) => ({ ...prev, stato: event.target.value }))}
              >
                {statoOptions.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                options={assigneeOptions}
                value={form.assegnatario}
                onChange={(_, value) => setForm((prev) => ({ ...prev, assegnatario: value || null }))}
                getOptionLabel={(option) => option?.name || ''}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Assegnatario"
                    helperText="Utente interno che segue operativamente la pratica"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={4}
                label="Note interne"
                value={form.note_interne}
                onChange={(event) => setForm((prev) => ({ ...prev, note_interne: event.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>
            Annulla
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Salvataggio...' : editingRow ? 'Salva modifiche' : 'Crea pratica'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={documentsDialogOpen} onClose={handleCloseDocumentsDialog} fullWidth maxWidth="lg">
        <DialogTitle sx={{ px: 2.5, py: 1.75 }}>
          {selectedPracticeForDocuments?.titolo
            ? `Documenti richiesti - ${selectedPracticeForDocuments.titolo}`
            : 'Documenti richiesti'}
        </DialogTitle>
        <DialogContent dividers sx={{ px: 2.5, py: 2 }}>
          {generationSubmitting ? (
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.75 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {getGenerationProgressLabel(generationProgress, generationPhase)}
                </Typography>
                <Typography
                  variant="body2"
                  color={`${getGenerationProgressChipColor(generationProgress)}.main`}
                  sx={{ fontWeight: 700 }}
                >
                  {Math.round(generationProgress)}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={generationProgress}
                sx={{
                  height: 10,
                  borderRadius: 999,
                  bgcolor: 'grey.300',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 999,
                    background: 'linear-gradient(90deg, #6366f1 0%, #8b5cf6 55%, #06b6d4 100%)'
                  }
                }}
              />
              <Alert severity="info" sx={{ mt: 1.5 }}>
                L&apos;AI sta lavorando alla bozza della pratica. Fase: {getGenerationProgressLabel(generationProgress, generationPhase)}. Avanzamento: {Math.round(generationProgress)}%.
              </Alert>
            </Box>
          ) : null}

          <Tabs
            value={documentsDialogTab}
            onChange={(_event, value) => setDocumentsDialogTab(value)}
            sx={{ mb: 2, borderBottom: '1px solid', borderColor: 'divider' }}
          >
            <Tab value="documents" label="Documenti richiesti" />
            <Tab value="result" label="Bozza proposta" disabled={!latestGenerationResult && !generationSubmitting} />
            <Tab value="history" label={`Storico generazioni (${practiceGenerationStats.total_count || 0})`} />
          </Tabs>

          {documentsDialogTab === 'documents' ? (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.3, fontSize: 13 }}>
                    Elenco corrente dei documenti richiesti per il template della pratica. I file caricati completano la checklist operativa.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1 }}>
                    <Chip size="small" variant="outlined" label={`${documentsData.summary.totalTemplateDocuments} documenti`} />
                    <Chip
                      size="small"
                      color="warning"
                      variant="outlined"
                      label={`${documentsData.summary.totalRequiredDocuments} obbligatori`}
                    />
                    <Chip
                      size="small"
                      color={generationReady ? 'success' : 'default'}
                      variant={generationReady ? 'filled' : 'outlined'}
                      label={
                        generationReady
                          ? 'Validazione ok'
                          : `${missingRequiredChecklistItems.length} mancanti`
                      }
                    />
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap', whiteSpace: 'nowrap' }}>
                  <Tooltip title="Includi anche i documenti facoltativi nella creazione dei demo">
                    <span>
                      <Checkbox
                        size="small"
                        sx={{ p: 0.5 }}
                        checked={includeOptionalDemo}
                        onChange={(event) => setIncludeOptionalDemo(event.target.checked)}
                        disabled={documentsSaving || documentsLoading || generationSubmitting || demoDocumentsSubmitting}
                      />
                    </span>
                  </Tooltip>
                  <Tooltip
                    title={
                      missingDemoChecklistItems.length === 0
                        ? includeOptionalDemo
                          ? 'Nessun documento mancante (obbligatori o facoltativi)'
                          : 'Nessun documento obbligatorio mancante'
                        : includeOptionalDemo
                        ? 'Crea automaticamente PDF demo per i documenti mancanti (obbligatori e facoltativi)'
                        : 'Crea automaticamente PDF demo per i documenti obbligatori mancanti'
                    }
                  >
                    <span>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<FileTextIcon size={15} />}
                        onClick={handleGenerateDemoDocuments}
                        disabled={
                          documentsSaving ||
                          documentsLoading ||
                          generationSubmitting ||
                          demoDocumentsSubmitting ||
                          missingDemoChecklistItems.length === 0
                        }
                      >
                        {demoDocumentsSubmitting ? 'Creazione demo...' : 'Crea demo'}
                      </Button>
                    </span>
                  </Tooltip>
                  <Button
                    component="label"
                    variant="contained"
                    size="small"
                    startIcon={<PlusIcon size={15} />}
                    disabled={documentsSaving || generationSubmitting || demoDocumentsSubmitting}
                  >
                    Aggiungi documento
                    <input
                      hidden
                      type="file"
                      multiple
                      onChange={(event) => {
                        const files = Array.from(event.target.files || []);
                        event.target.value = '';
                        if (files.length) {
                          handleUploadDocument(null, files);
                        }
                      }}
                    />
                  </Button>
                </Box>
              </Box>

              {!generationReady && documentsData.checklist.length > 0 ? (
                <Typography variant="body2" color="warning.main" sx={{ mb: 1.5 }}>
                  Documenti obbligatori mancanti: {missingRequiredChecklistItems.map((item) => item.nome).join(', ')}.
                </Typography>
              ) : null}

              <TableContainer sx={{ mb: 2 }}>
                <Table size="small" sx={{ tableLayout: 'fixed' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: 48, py: 0.75, px: 0.75 }}>#</TableCell>
                      <TableCell sx={{ width: '31%', py: 0.75, px: 0.75 }}>Nome</TableCell>
                      <TableCell sx={{ py: 0.75, px: 0.75 }}>Descrizione</TableCell>
                      <TableCell sx={{ width: 92, py: 0.75, px: 0.75 }}>Obbl.</TableCell>
                      <TableCell sx={{ width: 110, py: 0.75, px: 0.75 }}>Stato</TableCell>
                      <TableCell align="right" sx={{ width: 170, py: 0.75, px: 0.75 }}>Azioni</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {documentsLoading ? (
                      <TableRow>
                        <TableCell colSpan={6}>
                          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                            Caricamento documenti...
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : documentsData.checklist.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6}>
                          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                            Il template non ha ancora documenti richiesti configurati.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      documentsData.checklist.map((item, index) => (
                        <TableRow
                          key={item.id}
                          hover
                          sx={{
                            '& .MuiTableCell-root': {
                              py: 0.5,
                              px: 0.75,
                              verticalAlign: 'middle'
                            }
                          }}
                        >
                          <TableCell sx={{ fontWeight: 600 }}>{index + 1}</TableCell>
                          <TableCell>
                            <Box
                              sx={{
                                minHeight: 36,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1.5,
                                px: 1.25,
                                display: 'flex',
                                alignItems: 'center',
                                fontSize: 13,
                                color: 'text.primary',
                                bgcolor: 'background.paper'
                              }}
                            >
                              {item.nome}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box
                              sx={{
                                minHeight: 36,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1.5,
                                px: 1.25,
                                display: 'flex',
                                alignItems: 'center',
                                fontSize: 13,
                                color: 'text.secondary',
                                bgcolor: 'background.paper',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                              title={item.descrizione || ''}
                            >
                              {item.descrizione || '-'}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35, flexWrap: 'nowrap' }}>
                              <Switch
                                checked={Boolean(item.obbligatorio)}
                                size="small"
                                disabled
                                sx={{ mr: 0, ml: -0.5 }}
                              />
                              <Chip
                                size="small"
                                color={item.obbligatorio ? 'warning' : 'default'}
                                variant={item.obbligatorio ? 'filled' : 'outlined'}
                                label={item.obbligatorio ? 'Obbl.' : 'Fac.'}
                                sx={{ height: 20, '& .MuiChip-label': { px: 0.75, fontSize: 11 } }}
                              />
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              color={item.uploadedCount > 0 ? 'success' : item.obbligatorio ? 'warning' : 'default'}
                              variant={item.uploadedCount > 0 ? 'filled' : 'outlined'}
                              label={item.uploadedCount > 0 ? `Caricato (${item.uploadedCount})` : 'Mancante'}
                              sx={{ height: 20, '& .MuiChip-label': { px: 0.75, fontSize: 11 } }}
                            />
                          </TableCell>
                          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'nowrap', gap: 0.25 }}>
                              <Tooltip title="Carica file">
                                <IconButton
                                  component="label"
                                  size="small"
                                  color="primary"
                                  disabled={documentsSaving || generationSubmitting || demoDocumentsSubmitting}
                                >
                                  <UploadIcon size={16} />
                                  <input
                                    hidden
                                    type="file"
                                    multiple
                                    onChange={(event) => {
                                      const files = Array.from(event.target.files || []);
                                      event.target.value = '';
                                      if (files.length) {
                                        handleUploadDocument(item.id, files);
                                      }
                                    }}
                                  />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={item.latestUpload ? `Preview ${item.latestUpload.nome_file}` : 'Nessun file disponibile'}>
                                <span>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    disabled={!item.latestUpload}
                                    onClick={() => item.latestUpload && handleDocumentUrl(item.latestUpload.id, 'preview')}
                                  >
                                    <EyeIcon size={16} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title={item.latestUpload ? `Download ${item.latestUpload.nome_file}` : 'Nessun file disponibile'}>
                                <span>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    disabled={!item.latestUpload}
                                    onClick={() => item.latestUpload && handleDocumentUrl(item.latestUpload.id, 'download')}
                                  >
                                    <DownloadIcon size={16} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title={item.latestUpload ? `Elimina ${item.latestUpload.nome_file}` : 'Nessun file disponibile'}>
                                <span>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    disabled={!item.latestUpload || generationSubmitting || demoDocumentsSubmitting}
                                    onClick={() => item.latestUpload && handleOpenDeleteDocumentDialog(item.latestUpload)}
                                  >
                                    <DeleteIcon size={16} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Documenti caricati fuori checklist
              </Typography>

              <TableContainer>
                <Table size="small" sx={{ tableLayout: 'fixed' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ py: 0.75, px: 0.75 }}>Nome file</TableCell>
                      <TableCell sx={{ width: 150, py: 0.75, px: 0.75 }}>Mime</TableCell>
                      <TableCell sx={{ width: 160, py: 0.75, px: 0.75 }}>Caricato il</TableCell>
                      <TableCell sx={{ width: 180, py: 0.75, px: 0.75 }}>Utente</TableCell>
                      <TableCell align="right" sx={{ width: 132, py: 0.75, px: 0.75 }}>Azioni</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {documentsLoading ? (
                      <TableRow>
                        <TableCell colSpan={5}>
                          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                            Caricamento documenti...
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : documentsData.freeDocuments.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5}>
                          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                            Nessun documento libero caricato.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      documentsData.freeDocuments.map((documentRow) => (
                        <TableRow
                          key={documentRow.id}
                          hover
                          sx={{
                            '& .MuiTableCell-root': {
                              py: 0.5,
                              px: 0.75,
                              verticalAlign: 'middle'
                            }
                          }}
                        >
                          <TableCell>
                            <Box
                              sx={{
                                minHeight: 36,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1.5,
                                px: 1.25,
                                display: 'flex',
                                alignItems: 'center',
                                fontSize: 13,
                                color: 'text.primary',
                                bgcolor: 'background.paper',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                              title={documentRow.nome_file}
                            >
                              {documentRow.nome_file}
                            </Box>
                          </TableCell>
                          <TableCell>{documentRow.mime_type || '-'}</TableCell>
                          <TableCell>{formatDateTimeIt(documentRow.created_at)}</TableCell>
                          <TableCell>{documentRow.caricato_da_nome || '-'}</TableCell>
                          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'nowrap', gap: 0.25 }}>
                              <Tooltip title="Preview">
                                <IconButton size="small" color="primary" onClick={() => handleDocumentUrl(documentRow.id, 'preview')}>
                                  <EyeIcon size={16} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Download">
                                <IconButton size="small" color="primary" onClick={() => handleDocumentUrl(documentRow.id, 'download')}>
                                  <DownloadIcon size={16} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Elimina">
                                <IconButton
                                  size="small"
                                  color="error"
                                  disabled={generationSubmitting || demoDocumentsSubmitting}
                                  onClick={() => handleOpenDeleteDocumentDialog(documentRow)}
                                >
                                  <DeleteIcon size={16} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : documentsDialogTab === 'result' ? (
            <>
              {latestGenerationResult ? (
                <>
                  <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 2 }}>
                    <Chip size="small" color="success" variant="filled" label={latestGenerationResult?.stato || 'completata'} />
                    <Chip
                      size="small"
                      variant="outlined"
                      label={latestGenerationResult?.modello_ai_nome_visualizzazione || latestGenerationResult?.modello_ai_snapshot || 'Modello n.d.'}
                    />
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`Input ${formatGenerationTokenValue(latestGenerationResult?.token_input)} tok`}
                    />
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`Output ${formatGenerationTokenValue(latestGenerationResult?.token_output)} tok`}
                    />
                    {latestGenerationResult?.token_totali != null ? (
                      <Chip
                        size="small"
                        color="secondary"
                        variant="outlined"
                        label={`Totali ${formatGenerationTokenValue(latestGenerationResult?.token_totali)} tok`}
                      />
                    ) : null}
                    {latestGenerationResult?.costo_usd != null ? (
                      <Chip size="small" color="primary" variant="outlined" label={`Costo stimato $${latestGenerationResult.costo_usd}`} />
                    ) : null}
                  </Box>

                  <Alert severity="info" sx={{ mb: 2 }}>
                    La generazione è stata registrata in <code>gtl_generazione</code> e la pratica è stata aggiornata allo stato operativo successivo quando applicabile.
                  </Alert>

                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    Output AI
                  </Typography>
                  {latestGenerationResult?.stato === 'errore' ? (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {latestGenerationResult?.codice_errore ? `Codice: ${latestGenerationResult.codice_errore}. ` : ''}
                      {latestGenerationResult?.messaggio_errore || 'Dettaglio errore non disponibile.'}
                    </Alert>
                  ) : null}
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'grey.50',
                      maxHeight: '60vh',
                      overflowY: 'auto'
                    }}
                  >
                    {String(latestGenerationResult?.output_format || '').toLowerCase() === 'html' ? (
                      <Box sx={{ borderRadius: 1.5, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
                        <iframe
                          title={`generazione-${latestGenerationResult.id || 'preview'}`}
                          sandbox=""
                          style={{ width: '100%', height: '56vh', border: 0, background: 'transparent' }}
                          srcDoc={`<!doctype html><html lang="it"><head><meta charset="utf-8" /><style>
                            body{font-family:"Times New Roman",Times,serif;line-height:1.55;margin:28px 34px;font-size:12pt;color:${
                              theme.palette.mode === 'dark' ? '#e5e7eb' : '#111827'
                            };background:${theme.palette.mode === 'dark' ? '#0b1220' : '#ffffff'};}
                            p{margin:0 0 10px 0;}
                          </style></head><body>${latestGenerationResult?.output || ''}</body></html>`}
                        />
                      </Box>
                    ) : (
                      <Typography
                        variant="body2"
                        component="pre"
                        sx={{
                          m: 0,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          fontFamily: 'inherit'
                        }}
                      >
                        {latestGenerationResult?.output || 'Nessun contenuto generato disponibile.'}
                      </Typography>
                    )}
                  </Box>
                </>
              ) : (
                <Alert severity="info">
                  {generationSubmitting
                    ? 'La bozza sarà disponibile in questa tab al termine della generazione.'
                    : 'Nessuna bozza generata disponibile per questa pratica.'}
                </Alert>
              )}
            </>
          ) : (
            <>
              <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 2 }}>
                <Chip size="small" variant="outlined" label={`Totali ${practiceGenerationStats.total_count}`} />
                <Chip size="small" color="success" variant="outlined" label={`Completate ${practiceGenerationStats.completata_count}`} />
                <Chip size="small" color="info" variant="outlined" label={`In corso ${practiceGenerationStats.in_corso_count}`} />
                <Chip size="small" color="error" variant="outlined" label={`Errori ${practiceGenerationStats.errore_count}`} />
              </Box>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Creata il</TableCell>
                      <TableCell>Stato</TableCell>
                      <TableCell>Modello</TableCell>
                      <TableCell>Token</TableCell>
                      <TableCell>Costo</TableCell>
                      <TableCell>Completata il</TableCell>
                      <TableCell align="right">Azioni</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {practiceGenerationsLoading ? (
                      <TableRow>
                        <TableCell colSpan={8}>
                          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                            Caricamento storico generazioni...
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : practiceGenerations.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8}>
                          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                            Nessuna generazione disponibile per questa pratica.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      practiceGenerations.map((generation) => (
                        <TableRow hover key={generation.id}>
                          <TableCell>{generation.id}</TableCell>
                          <TableCell>{formatDateTimeIt(generation.created_at)}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              color={generation.stato === 'completata' ? 'success' : generation.stato === 'errore' ? 'error' : 'info'}
                              variant="filled"
                              label={generation.stato || '-'}
                              sx={{ fontWeight: 600 }}
                            />
                          </TableCell>
                          <TableCell>{generation.modello_ai_nome_visualizzazione || generation.modello_ai_snapshot || '-'}</TableCell>
                          <TableCell>{formatGenerationTokenValue(generation.token_totali)}</TableCell>
                          <TableCell>{generation.costo_usd != null ? `$${generation.costo_usd}` : '-'}</TableCell>
                          <TableCell>{formatDateTimeIt(generation.completata_at)}</TableCell>
                          <TableCell align="right">
                            <Tooltip title="Apri in bozza proposta">
                              <IconButton size="small" color="primary" onClick={() => handleSelectGenerationFromHistory(generation)}>
                                <EyeIcon size={16} />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5 }}>
          <Button
            onClick={handleCloseDocumentsDialog}
            disabled={documentsSaving || generationSubmitting || demoDocumentsSubmitting}
          >
            Chiudi
          </Button>
          {documentsDialogTab === 'result' ? (
            <Button onClick={() => setDocumentsDialogTab('documents')} disabled={generationSubmitting}>
              Torna ai documenti
            </Button>
          ) : null}
          <Button
            variant="contained"
            onClick={handleStartGeneration}
            disabled={
              documentsDialogTab !== 'documents' ||
              documentsSaving ||
              documentsLoading ||
              generationSubmitting ||
              demoDocumentsSubmitting ||
              !generationReady
            }
          >
            {generationSubmitting ? 'Generazione in corso...' : 'Avvia generazione'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={documentDeleteDialogOpen} onClose={handleCloseDeleteDocumentDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Elimina documento</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Stai per eliminare definitivamente il documento selezionato dalla pratica.
          </Typography>
          <Box
            sx={{
              mt: 1.5,
              p: 1.5,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'grey.50'
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Documento
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
              {documentDeleteTarget?.nome_file || '-'}
            </Typography>
            {documentDeleteTarget?.created_at ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                Caricato il {formatDateTimeIt(documentDeleteTarget.created_at)}
              </Typography>
            ) : null}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            L&apos;operazione rimuove sia il riferimento in archivio sia il file dallo storage collegato, quando presente.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDocumentDialog} disabled={documentsSaving}>
            Annulla
          </Button>
          <Button color="error" variant="contained" onClick={handleDeleteDocument} disabled={documentsSaving}>
            {documentsSaving ? 'Eliminazione...' : 'Elimina documento'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => !saving && setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Elimina pratica AI</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            Confermi l&apos;eliminazione della pratica <strong>{deleteTarget?.titolo || '-'}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (saving) return;
              setDeleteDialogOpen(false);
              setDeleteTarget(null);
            }}
          >
            Annulla
          </Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={saving}>
            {saving ? 'Eliminazione...' : 'Elimina'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
